<template>
  <div class="admin-results-page">
    <el-card class="box-card" shadow="never">
      <!-- HEADER -->
      <div slot="header" class="card-header">
        <div>
          <h2 class="page-title">Результати опитувань споживачів</h2>
          <span class="page-subtitle">Перегляд, детальний аналіз, фільтрація та експорт заповнених анкет у формат Excel</span>
        </div>
        <div class="header-actions">
          <el-button
            type="primary"
            size="small"
            :loading="exportingNkreckp"
            @click="handleExportNkreckp"
          >
            <i class="el-icon-download"></i> Звіт для НКРЕКП (.xlsx)
          </el-button>
          <el-button
            type="success"
            size="small"
            :loading="exportingExpanded"
            @click="handleExportExpanded"
          >
            <i class="el-icon-download"></i> Розширений звіт (.xlsx)
          </el-button>
        </div>
      </div>

      <!-- FILTER TOOLBAR -->
      <div class="filter-toolbar">
        <el-form :inline="true" :model="filters" size="small" class="filters-form">
          <el-form-item label="Опитування:">
            <el-select
              v-model="filters.surveyId"
              placeholder="Всі опитування"
              clearable
              style="width: 260px"
              @change="loadResponses"
            >
              <el-option
                v-for="s in surveysList"
                :key="s._id"
                :label="s.title"
                :value="s._id"
              />
            </el-select>
          </el-form-item>

          <el-form-item label="Тип споживача:">
            <el-select
              v-model="filters.consumerType"
              placeholder="Всі типи"
              clearable
              style="width: 160px"
              @change="loadResponses"
            >
              <el-option label="Побутовий" value="household" />
              <el-option label="Непобутовий" value="non_household" />
            </el-select>
          </el-form-item>

          <el-form-item label="Період:">
            <el-date-picker
              v-model="filters.dateRange"
              type="daterange"
              range-separator="до"
              start-placeholder="Від"
              end-placeholder="До"
              style="width: 230px"
              @change="loadResponses"
            />
          </el-form-item>

          <el-form-item label="Пошук за EIC:">
            <el-input
              v-model="filters.eic"
              placeholder="EIC-код..."
              clearable
              prefix-icon="el-icon-search"
              style="width: 190px"
              @keyup.enter.native="loadResponses"
              @clear="loadResponses"
            />
          </el-form-item>

          <el-form-item>
            <el-button type="primary" icon="el-icon-search" @click="loadResponses">
              Знайти
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="resetFilters">
              Скинути
            </el-button>
          </el-form-item>
        </el-form>
      </div>

      <!-- RESULTS TABLE -->
      <el-table
        :data="responsesList"
        v-loading="isLoading"
        stripe
        style="width: 100%"
        class="results-table"
      >
        <el-table-column prop="eic" label="EIC-код" width="200">
          <template slot-scope="scope">
            <span class="eic-badge">{{ scope.row.eic }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Тип споживача" width="160">
          <template slot-scope="scope">
            <el-tag
              :type="scope.row.consumerType === 'household' ? 'success' : 'warning'"
              size="small"
            >
              {{ scope.row.consumerType === 'household' ? 'Побутовий' : 'Непобутовий' }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Джерело" width="160">
          <template slot-scope="scope">
            <span class="source-tag">
              <i :class="scope.row.entrySource === 'portal' ? 'el-icon-user' : 'el-icon-link'"></i>
              {{ scope.row.entrySource === 'portal' ? 'Особистий кабінет' : 'Веб-посилання' }}
            </span>
          </template>
        </el-table-column>

        <el-table-column label="Населений пункт" min-width="160">
          <template slot-scope="scope">
            <span>{{ (scope.row.consumerData && scope.row.consumerData.settlementName) || '—' }}</span>
          </template>
        </el-table-column>

        <el-table-column label="Дата та час" width="170">
          <template slot-scope="scope">
            <span>{{ formatDateTime(scope.row.completedAt) }}</span>
          </template>
        </el-table-column>

        <el-table-column label="К-ть відповідей" width="130" align="center">
          <template slot-scope="scope">
            <el-tag size="mini" type="info">
              {{ (scope.row.answers && scope.row.answers.length) || 0 }} відп.
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Дії" width="190" align="right">
          <template slot-scope="scope">
            <el-button
              size="mini"
              type="primary"
              plain
              icon="el-icon-document"
              @click="openDetailDialog(scope.row)"
            >
              Анкета
            </el-button>
            <el-button
              size="mini"
              type="danger"
              plain
              icon="el-icon-delete"
              @click="confirmDeleteResponse(scope.row)"
            >
              Видалити
            </el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- PAGINATION -->
      <div class="pagination-wrap">
        <el-pagination
          background
          layout="total, prev, pager, next, sizes"
          :current-page.sync="page"
          :page-size.sync="limit"
          :page-sizes="[10, 20, 50, 100]"
          :total="totalResponses"
          @current-change="loadResponses"
          @size-change="loadResponses"
        />
      </div>
    </el-card>

    <!-- DETAILED RESPONSE MODAL -->
    <el-dialog
      title="Детальний перегляд заповненої анкети"
      :visible.sync="detailDialogVisible"
      width="750px"
      custom-class="detail-dialog"
    >
      <div v-if="selectedResponse" class="response-detail-content">
        <!-- Consumer Metadata -->
        <div class="meta-section">
          <div class="meta-item">
            <span class="meta-label">EIC-код:</span>
            <span class="meta-value font-mono">{{ selectedResponse.eic }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Тип споживача:</span>
            <el-tag size="mini" :type="selectedResponse.consumerType === 'household' ? 'success' : 'warning'">
              {{ selectedResponse.consumerType === 'household' ? 'Побутовий' : 'Непобутовий' }}
            </el-tag>
          </div>
          <div class="meta-item">
            <span class="meta-label">Джерело:</span>
            <span class="meta-value">{{ selectedResponse.entrySource === 'portal' ? 'Особистий кабінет' : 'Веб-посилання' }}</span>
          </div>
          <div class="meta-item">
            <span class="meta-label">Дата завершення:</span>
            <span class="meta-value">{{ formatDateTime(selectedResponse.completedAt) }}</span>
          </div>
          <div v-if="selectedResponse.consumerData && selectedResponse.consumerData.settlementName" class="meta-item">
            <span class="meta-label">Населений пункт:</span>
            <span class="meta-value">{{ selectedResponse.consumerData.settlementName }}</span>
          </div>
          <div v-if="selectedResponse.consumerData && selectedResponse.consumerData.annualConsumption" class="meta-item">
            <span class="meta-label">Річне споживання:</span>
            <span class="meta-value">{{ selectedResponse.consumerData.annualConsumption }} м³</span>
          </div>
        </div>

        <el-divider content-position="left">Надані відповіді</el-divider>

        <!-- Answers List -->
        <div class="answers-container">
          <div
            v-for="(ans, aIndex) in selectedResponse.answers"
            :key="aIndex"
            class="answer-item-card"
          >
            <div class="ans-header">
              <span class="ans-code">[{{ ans.questionCode }}]</span>
              <span class="ans-text">{{ ans.questionText }}</span>
            </div>
            <div class="ans-value-box">
              <span class="ans-label">Відповідь:</span>
              <span class="ans-value">
                <template v-if="Array.isArray(ans.value)">
                  {{ ans.value.join(', ') }}
                </template>
                <template v-else>
                  {{ ans.value }}
                </template>
              </span>
            </div>
            <div v-if="ans.otherText" class="ans-other-box">
              <span class="ans-other-label">Уточнення до варіанта «Інше»:</span>
              <span class="ans-other-value">{{ ans.otherText }}</span>
            </div>
          </div>
        </div>
      </div>

      <span slot="footer" class="dialog-footer" style="display: flex; justify-content: space-between; align-items: center;">
        <el-button
          v-if="selectedResponse"
          size="small"
          type="danger"
          plain
          icon="el-icon-delete"
          @click="confirmDeleteResponse(selectedResponse)"
        >
          Видалити цю відповідь
        </el-button>
        <span v-else></span>
        <el-button size="small" @click="detailDialogVisible = false">Закрити</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import dayjs from 'dayjs';
import axios from 'axios';

export default {
  name: 'AdminSurveyResults',
  data() {
    return {
      page: 1,
      limit: 20,
      filters: {
        surveyId: '',
        consumerType: '',
        dateRange: [],
        eic: '',
      },
      exportingNkreckp: false,
      exportingExpanded: false,
      detailDialogVisible: false,
      selectedResponse: null,
    };
  },
  computed: {
    ...mapGetters('Survey', ['responsesList', 'totalResponses', 'surveysList', 'isLoading']),
  },
  created() {
    if (this.$route.query.surveyId) {
      this.filters.surveyId = this.$route.query.surveyId;
    }
    this.fetchAdminSurveys();
    this.loadResponses();
  },
  methods: {
    ...mapActions('Survey', ['fetchAdminResponses', 'fetchAdminSurveys', 'deleteSurveyResponse']),

    async confirmDeleteResponse(response) {
      if (!response || !response._id) return;
      try {
        await this.$confirm(
          `Ви дійсно бажаєте видалити цю відповідь (EIC: ${response.eic})? Після видалення споживач зможе повторно пройти опитування.`,
          'Видалення анкети опитування',
          {
            confirmButtonText: 'Видалити',
            cancelButtonText: 'Скасувати',
            type: 'warning',
            confirmButtonClass: 'el-button--danger',
          }
        );

        await this.deleteSurveyResponse(response._id);
        this.$message.success(`Відповідь споживача (${response.eic}) успішно видалено`);

        if (this.detailDialogVisible && this.selectedResponse && this.selectedResponse._id === response._id) {
          this.detailDialogVisible = false;
          this.selectedResponse = null;
        }

        this.loadResponses();
      } catch (action) {
        if (action !== 'cancel') {
          console.error('Error deleting response:', action);
          this.$message.error('Помилка при видаленні відповіді');
        }
      }
    },

    formatDateTime(date) {
      if (!date) return '';
      return dayjs(date).format('DD.MM.YYYY HH:mm');
    },

    loadResponses() {
      const params = {
        page: this.page,
        limit: this.limit,
      };

      if (this.filters.surveyId) params.surveyId = this.filters.surveyId;
      if (this.filters.consumerType) params.consumerType = this.filters.consumerType;
      if (this.filters.eic) params.eic = this.filters.eic.trim();

      if (this.filters.dateRange && this.filters.dateRange.length === 2) {
        params.dateFrom = dayjs(this.filters.dateRange[0]).format('YYYY-MM-DD');
        params.dateTo = dayjs(this.filters.dateRange[1]).format('YYYY-MM-DD');
      }

      this.fetchAdminResponses(params);
    },

    resetFilters() {
      this.filters = {
        surveyId: '',
        consumerType: '',
        dateRange: [],
        eic: '',
      };
      this.page = 1;
      this.loadResponses();
    },

    openDetailDialog(response) {
      this.selectedResponse = response;
      this.detailDialogVisible = true;
    },

    buildFilterParams() {
      const params = {};
      if (this.filters.surveyId) params.surveyId = this.filters.surveyId;
      if (this.filters.consumerType) params.consumerType = this.filters.consumerType;
      if (this.filters.eic) params.eic = this.filters.eic.trim();
      if (this.filters.dateRange && this.filters.dateRange.length === 2) {
        params.dateFrom = dayjs(this.filters.dateRange[0]).format('YYYY-MM-DD');
        params.dateTo = dayjs(this.filters.dateRange[1]).format('YYYY-MM-DD');
      }
      return params;
    },

    async handleExportNkreckp() {
      this.exportingNkreckp = true;
      try {
        this.$message.info('Генерація офіційного звіту для НКРЕКП...');
        const params = this.buildFilterParams();

        const response = await axios.get('/api/survey/admin/export-nkreckp', {
          params,
          responseType: 'blob',
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = `Survey_NKREKP_${dayjs().format('YYYY-MM-DD_HH-mm')}.xlsx`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$message.success('Звіт для НКРЕКП успішно завантажено');
      } catch (err) {
        this.$message.error('Не вдалося сформувати звіт для НКРЕКП');
      } finally {
        this.exportingNkreckp = false;
      }
    },

    async handleExportExpanded() {
      this.exportingExpanded = true;
      try {
        this.$message.info('Генерація розширеного Excel-звіту...');
        const params = this.buildFilterParams();

        const response = await axios.get('/api/survey/admin/export-xlsx', {
          params,
          responseType: 'blob',
        });

        const blob = new Blob([response.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = `Survey_Results_Expanded_${dayjs().format('YYYY-MM-DD_HH-mm')}.xlsx`;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.$message.success('Розширений звіт успішно експортовано');
      } catch (err) {
        this.$message.error('Не вдалося сформувати розширений Excel-звіт');
      } finally {
        this.exportingExpanded = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.admin-results-page {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  .page-title {
    font-size: 20px;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 4px 0;
  }

  .page-subtitle {
    font-size: 13px;
    color: #64748b;
  }
}

.filter-toolbar {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px 16px 4px 16px;
  margin-bottom: 20px;

  .filters-form {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  }
}

.results-table {
  .eic-badge {
    font-family: monospace;
    font-weight: 700;
    color: #0f172a;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .source-tag {
    font-size: 13px;
    color: #475569;
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.pagination-wrap {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.response-detail-content {
  .meta-section {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 14px;
    margin-bottom: 20px;

    .meta-item {
      display: flex;
      flex-direction: column;
      gap: 2px;

      .meta-label {
        font-size: 12px;
        color: #64748b;
      }

      .meta-value {
        font-size: 14px;
        font-weight: 600;
        color: #1e293b;

        &.font-mono {
          font-family: monospace;
        }
      }
    }
  }

  .answers-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    max-height: 480px;
    overflow-y: auto;
    padding-right: 6px;

    .answer-item-card {
      background: #ffffff;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      padding: 12px 14px;

      .ans-header {
        display: flex;
        align-items: flex-start;
        gap: 6px;
        margin-bottom: 8px;

        .ans-code {
          font-size: 12px;
          font-weight: 700;
          color: #3b82f6;
          flex-shrink: 0;
        }

        .ans-text {
          font-size: 14px;
          font-weight: 600;
          color: #1e293b;
        }
      }

      .ans-value-box {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 13px;

        .ans-label {
          color: #64748b;
        }

        .ans-value {
          color: #10b981;
          font-weight: 700;
        }
      }

      .ans-other-box {
        margin-top: 6px;
        font-size: 13px;
        background: #f1f5f9;
        padding: 4px 8px;
        border-radius: 4px;

        .ans-other-label {
          color: #64748b;
          margin-right: 4px;
        }

        .ans-other-value {
          color: #1e293b;
          font-weight: 600;
        }
      }
    }
  }
}
</style>
